
import { prop, Vue, Options } from "vue-class-component";
import Calendar from "primevue/calendar";
import CheckboxV2 from "@/app/ui/components/check-box-v2/index.vue";
import { generateSlotStarts, addOneHour, toIsoDate } from "./schedule-utils";
import { AccountController } from "@/app/ui/controllers/AccountController";

interface TimeSlot {
  start: string;
  end: string;
}

interface SlotOption {
  start: string;
  end: string;
  label: string;
}

class PickScheduleModalProps {
  open = prop<boolean>({ default: false, type: Boolean });
  existingDates = prop<string[]>({ default: () => [], type: Array });
  maxSlotsPerDate = prop<number>({ default: 3, type: Number });
  slotStartHour = prop<number>({ default: 8, type: Number });
  slotEndHour = prop<number>({ default: 23, type: Number });
  slotIntervalMinutes = prop<number>({ default: 60, type: Number });
  minDate = prop<Date | string>({
    default: () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      return tomorrow;
    }
  });
}

const ID_LOCALE = {
  firstDayOfWeek: 1, // Senin
  dayNames: ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"],
  dayNamesShort: ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"],
  dayNamesMin: ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"],
  monthNames: [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember"
  ],
  monthNamesShort: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "Mei",
    "Jun",
    "Jul",
    "Agu",
    "Sep",
    "Okt",
    "Nov",
    "Des"
  ],
  today: "Hari Ini",
  clear: "Bersihkan"
};

const EN_LOCALE = {
  firstDayOfWeek: 0, // Sunday
  dayNames: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
  ],
  dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  dayNamesMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  monthNames: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ],
  monthNamesShort: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
  ],
  today: "Today",
  clear: "Clear"
};

@Options({
  components: {
    Calendar,
    CheckboxV2
  },
  watch: {
    open(newVal: boolean) {
      if (newVal) {
        this.resetDraft();
        this.updateCalendarLocale();
      }
    }
  }
})
export default class PickScheduleModal extends Vue.with(
  PickScheduleModalProps
) {
  draftDate: Date | null = null;
  draftStarts: string[] = [];
  duplicateDateError = false;

  get minDateObj(): Date {
    if (typeof this.minDate === "string") {
      return new Date(this.minDate);
    }
    return this.minDate;
  }

  get disabledDateObjects(): Date[] {
    return this.existingDates.map((iso) => new Date(`${iso}T00:00:00`));
  }

  get slotStarts(): string[] {
    return generateSlotStarts(
      this.slotStartHour,
      this.slotEndHour,
      this.slotIntervalMinutes
    );
  }

  get slotOptions(): SlotOption[] {
    return this.slotStarts.map((start) => {
      const end = addOneHour(start);
      return {
        start,
        end,
        label: `${start} - ${end}`
      };
    });
  }

  get canSave(): boolean {
    return !!this.draftDate && this.draftStarts.length > 0;
  }

  mounted(): void {
    this.updateCalendarLocale();
  }

  updateCalendarLocale(): void {
    const countryCode =
      AccountController.accountData?.countryCode?.toLowerCase() || "id";
    const targetLocale = countryCode === "id" ? ID_LOCALE : EN_LOCALE;

    const primevue = (this as any).$primevue;
    if (primevue && primevue.config) {
      primevue.config.locale = {
        ...primevue.config.locale,
        ...targetLocale
      };
    }
  }

  isSlotDisabled(start: string): boolean {
    if (this.draftStarts.includes(start)) return false;
    return this.draftStarts.length >= this.maxSlotsPerDate;
  }

  toggleSlot(start: string, checked: boolean): void {
    if (checked) {
      if (this.draftStarts.length < this.maxSlotsPerDate) {
        this.draftStarts = [...this.draftStarts, start];
      }
    } else {
      this.draftStarts = this.draftStarts.filter((s) => s !== start);
    }
  }

  onDateSelect(): void {
    this.duplicateDateError = false;
  }

  onCloseModal(): void {
    this.resetDraft();
    this.$emit("update:open", false);
  }

  resetDraft(): void {
    this.draftDate = null;
    this.draftStarts = [];
    this.duplicateDateError = false;
  }

  onSave(): void {
    if (!this.canSave || !this.draftDate) return;

    const isoDate = toIsoDate(this.draftDate);

    // Defense-in-depth: Cek duplikasi tanggal
    if (this.existingDates.includes(isoDate)) {
      this.duplicateDateError = true;
      this.$emit("duplicate-date-rejected");
      return;
    }

    // Urutkan slot jam secara ascending
    const sortedSlots: TimeSlot[] = [...this.draftStarts]
      .sort()
      .map((start) => ({
        start,
        end: addOneHour(start)
      }));

    this.$emit("save", {
      date: isoDate,
      slots: sortedSlots
    });

    this.onCloseModal();
  }
}
